import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';

// Supported languages
export const LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹' },
    { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
    { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪' },
];

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('mediguide_language');
        return saved || 'en';
    });

    const [isRTL, setIsRTL] = useState(false);
    
    // Track if user has completed initial language setup
    const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => {
        return localStorage.getItem('mediguide_language_selected') === 'true';
    });

    useEffect(() => {
        localStorage.setItem('mediguide_language', language);
        
        // Check if language is RTL
        const langConfig = LANGUAGES.find(l => l.code === language);
        const rtl = langConfig?.rtl || false;
        setIsRTL(rtl);
        
        // Update document direction
        document.documentElement.dir = rtl ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
    }, [language]);

    // Mark language as selected (called after first-time selection)
    const confirmLanguageSelection = () => {
        localStorage.setItem('mediguide_language_selected', 'true');
        setHasSelectedLanguage(true);
    };

    // Translation function
    const t = (key) => {
        return translations[language]?.[key] || translations['en']?.[key] || key;
    };

    // Get current language config
    const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

    const value = {
        language,
        setLanguage,
        t,
        isRTL,
        currentLanguage,
        languages: LANGUAGES,
        hasSelectedLanguage,
        confirmLanguageSelection,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
